import { useCallback, useState } from "react";
import { vehicleService } from "../Services/vehicleService";
import { operationsService } from "../Services/operations.service";

function pickValue(source = {}, keys = []) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
}

function normalizeOperationDetailsToPageModel(rawDetails = {}, vehicle = {}) {
  const addressLine1 = pickValue(rawDetails, ["addressLine1", "AddressLine1"]);
  const addressLine2 = pickValue(rawDetails, ["addressLine2", "AddressLine2"]);

  const licensePlate = pickValue(rawDetails, ["licensePlate", "LicensePlate"]);
  const licensePlateAlias = pickValue(rawDetails, [
    "licensePlateAlias",
    "LicensePlateAlias",
  ]);
  const customerName = pickValue(rawDetails, ["customerName", "CustomerName"]);
  const customerCorporateName = pickValue(rawDetails, [
    "customerCorporateName",
    "CustomerCorporateName",
  ]);
  const customerDoc = pickValue(rawDetails, ["doc", "Doc"]);
  const vehicleNotes = pickValue(rawDetails, ["vehicleNotes", "VehicleNotes"]);
  const customerNotes = pickValue(rawDetails, ["customerNotes", "CustomerNotes"]);

  return {
    client: {
      name: customerName || vehicle.clientName || "",
      fantasyName: customerName || vehicle.clientName || "",
      cpfCnpj: customerDoc || vehicle.cpfCnpj || "",
      phone: pickValue(rawDetails, ["phoneNumber1", "PhoneNumber1"]),
      phone2: pickValue(rawDetails, ["phoneNumber2", "PhoneNumber2"]),
      addressLine1: addressLine1,
      addressLine2: addressLine2,
      email: pickValue(rawDetails, ["email", "Email"]),
      personType: "Pessoa Juridica",
      customerObservation: customerNotes || "",
      vehicleContractObservation: vehicleNotes || "",
      companyName: customerCorporateName || vehicle.companyName || "",
    },
    vehicle: {
      id: vehicle.id ?? vehicle.Id,
      brand: pickValue(rawDetails, ["manufacturer", "Manufacturer"]),
      model: pickValue(rawDetails, ["vehicleModel", "VehicleModel"]),
      plate: licensePlate || vehicle.plate || vehicle.licensePlate || "",
      alias: licensePlateAlias || vehicle.nickname || "",
      year: pickValue(rawDetails, ["modelYear", "ModelYear"]),
      manufactureYear: pickValue(rawDetails, ["manufactureYear", "ManufactureYear"]),
      color: pickValue(rawDetails, ["color", "Color"]),
      category: pickValue(rawDetails, ["vehicleCategory", "VehicleCategory"]),
      equipment: pickValue(rawDetails, ["deviceModel", "DeviceModel"]),
      serial: pickValue(rawDetails, ["serialNumber", "SerialNumber"]),
      carrier: pickValue(rawDetails, ["mobileOperator", "MobileOperator"]),
      lineNumber: pickValue(rawDetails, ["phoneLineNumber", "PhoneLineNumber"]),
      deviceManufacturer: pickValue(rawDetails, [
        "deviceManufacturer",
        "DeviceManufacturer",
      ]),
    },
    status: {
      hasBlocking: false,
      inMaintenance: false,
    },
    maintenance: {
      vehicleLabel: licensePlate || vehicle.plate || "",
      startDate: "",
      endDate: "",
    },
    internalObservation: vehicleNotes || "",
    commercialConditionId:
      rawDetails.commercialConditionId ?? rawDetails.CommercialConditionId ?? null,
  };
}

function normalizeSelectedVehicle(vehicle = {}, operationDetails = {}) {
  return {
    id: vehicle.id ?? vehicle.Id,
    clientId: vehicle.clientId ?? vehicle.ClientId ?? null,
    plate:
      vehicle.plate ||
      vehicle.licensePlate ||
      operationDetails.licensePlate ||
      operationDetails.LicensePlate ||
      "",
    nickname:
      vehicle.nickname ||
      vehicle.licensePlateAlias ||
      operationDetails.licensePlateAlias ||
      operationDetails.LicensePlateAlias ||
      "",
    clientName:
      vehicle.clientName || operationDetails.customerName || operationDetails.CustomerName || "",
    companyName:
      vehicle.companyName ||
      operationDetails.customerCorporateName ||
      operationDetails.CustomerCorporateName ||
      "",
    contractStatus:
      vehicle.contractStatus || operationDetails.ContractStatusDescription || "",
    cpfCnpj: vehicle.cpfCnpj || operationDetails.doc || operationDetails.Doc || "",
  };
}

function normalizeOperationsList(items = []) {
  if (!Array.isArray(items)) return [];

  return items.map((item, index) => ({
    id: item.Id ?? item.VehicleId ?? item.id ?? index,
    clientId:
      item.ClientId ?? item.clientId ?? item.CustomerId ?? item.customerId ?? null,
    plate: item.LicensePlate || item.licensePlate || "",
    nickname: item.LicensePlateAlias || item.licensePlateAlias || "",
    clientName: item.CustomerName || item.customerName || "",
    companyName:
      item.CustomerCorporateName || item.customerCorporateName || "",
    contractStatus:
      item.ContractStatusDescription ||
      item.contractStatusDescription ||
      "",
    cpfCnpj: item.Doc || item.doc || "",
  }));
}

export function useVehicle() {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [savingObservation, setSavingObservation] = useState(false);
  const [startingMaintenance, setStartingMaintenance] = useState(false);

  const [error, setError] = useState("");

  const searchVehicles = useCallback(async (filters) => {
    try {
      setError("");
      setLoadingVehicles(true);

      const operationsResult = await operationsService.fetchVehicleOperations({
        LicensePlate: filters?.plate,
        Doc: filters?.cpfCnpj,
        CustomerName: filters?.clientName,
      });

      const normalized = normalizeOperationsList(operationsResult);

      if (normalized.length) {
        setVehicles(normalized);
      } else {
        const fallback = await vehicleService.getVehicles(filters);
        setVehicles(fallback || []);
      }

      setIsModalOpen(true);
    } catch (err) {
      setError(err.message || "Erro ao buscar veiculos.");
    } finally {
      setLoadingVehicles(false);
    }
  }, []);

  const closeVehicleModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const selectVehicle = useCallback(async (vehicle) => {
    const vehicleId = vehicle?.id ?? vehicle?.Id;
    const clientId = vehicle?.clientId ?? vehicle?.ClientId;
    const operationId = clientId ?? vehicleId;

    if (!operationId && operationId !== 0) return;

    try {
      setError("");
      setLoadingDetails(true);

      const operationDetails = await operationsService.fetchVehicleOperationById(operationId);

      let details = null;

      if (operationDetails) {
        details = normalizeOperationDetailsToPageModel(operationDetails, vehicle);
      } else {
        details = await vehicleService.getVehicleDetails(vehicleId);
      }

      setSelectedVehicle(normalizeSelectedVehicle(vehicle, operationDetails || {}));
      setVehicleDetails(details);
      setIsModalOpen(false);
    } catch (err) {
      setError(err.message || "Erro ao carregar detalhes do veiculo.");
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  const saveObservation = useCallback(async (observationText) => {
    if (!selectedVehicle?.id) return;

    try {
      setError("");
      setSavingObservation(true);
      await vehicleService.saveObservation(selectedVehicle.id, observationText);
      setVehicleDetails((prev) => ({
        ...prev,
        internalObservation: observationText,
      }));
    } catch (err) {
      setError(err.message || "Erro ao salvar observacao.");
    } finally {
      setSavingObservation(false);
    }
  }, [selectedVehicle]);

  const startMaintenance = useCallback(async () => {
    if (!selectedVehicle?.id) return;

    try {
      setError("");
      setStartingMaintenance(true);
      const maintenance = await vehicleService.startMaintenance(selectedVehicle.id);
      setVehicleDetails((prev) => ({
        ...prev,
        maintenance,
        status: {
          ...prev?.status,
          inMaintenance: true,
        },
      }));
    } catch (err) {
      setError(err.message || "Erro ao iniciar manutencao.");
    } finally {
      setStartingMaintenance(false);
    }
  }, [selectedVehicle]);

  return {
    vehicles,
    selectedVehicle,
    vehicleDetails,
    isModalOpen,
    loadingVehicles,
    loadingDetails,
    savingObservation,
    startingMaintenance,
    error,
    searchVehicles,
    closeVehicleModal,
    selectVehicle,
    saveObservation,
    startMaintenance,
    setError,
  };
}
